import { queryOptions, type QueryClient } from "@tanstack/react-query";
import { getGuestFieldCatalogue } from "@/lib/api/clients/AdditionalFieldClient";
import { guestFieldCatalogueKey } from "@/lib/authz/roleTemplateQueryKeys";
import type { CatalogueEntry } from "@/lib/authz/fieldExclusion";

/** The catalogue query's fetcher, for a host that wraps it (the Roles badge times it). */
export const readGuestFieldCatalogue = (eventId: string) =>
  getGuestFieldCatalogue(eventId, { bypassPreview: true });

/**
 * B2 (contract §4): the ONE way to read the loud guest field catalogue — the
 * role editor page, the preview bar's editor and the Roles badge share key,
 * queryFn and meta (mixed meta on one key = whichever observer set its
 * options last). It rides the admin's own auth (`bypassPreview`), so a
 * preview re-mint leaves it alone (`meta.previewScopeIndependent`). It throws
 * on failure: a failed read is "unknown", never "no custom fields". Each host
 * adds its own `enabled` / `staleTime`.
 */
export const guestFieldCatalogueQuery = (eventId: string) =>
  queryOptions({
    queryKey: guestFieldCatalogueKey(eventId),
    queryFn: () => readGuestFieldCatalogue(eventId),
    meta: { previewScopeIndependent: true },
  });

/**
 * The pane's catalogue status from the query. B2 F2 (audit r2 N2): a retry
 * after a failed read stays "failed" while it runs — a refetch of a query
 * without data goes back to "pending", and the error box would vanish with
 * the (busy) Retry the focus is on; its button shows the retry instead.
 */
export function guestCatalogueStatus(q: {
  data?: unknown;
  isError: boolean;
  isFetching: boolean;
  errorUpdatedAt: number;
}): "ready" | "loading" | "failed" {
  if (q.data) return "ready";
  return q.isError || (q.isFetching && q.errorUpdatedAt > 0) ? "failed" : "loading";
}

/** B2 F2 (audit r2 N5): how long the uncached read may take before it counts as failed. */
export const FRESH_CATALOGUE_TIMEOUT_MS = 15_000;

/**
 * B2 F1 (contract §8, S-MED-1): the UNCACHED catalogue read (`fresh=true`) —
 * for the questions a cached list must not answer: the sensitive re-check
 * before every "All except" save, "Load latest" and the re-seed. Read
 * directly (never deduped onto a cached read already in flight), then written
 * under the shared key so the pane shows what the check saw. Throws on
 * failure and leaves the cache alone.
 *
 * B2 F2 (audit r2): a read that has not answered after
 * `FRESH_CATALOGUE_TIMEOUT_MS` is aborted and FAILS (N5) — a Save must not
 * sit on "Saving…" forever. And a cached read this one cancelled while the
 * query had no data yet (its first load) is started again when the fresh
 * read fails (N1): cancelling it left the query pending with nothing out, and
 * the pane on "Loading…" with no retry.
 */
export async function refreshGuestFieldCatalogue(
  queryClient: QueryClient,
  eventId: string,
): Promise<CatalogueEntry[]> {
  const key = guestFieldCatalogueKey(eventId);
  const before = queryClient.getQueryState(key);
  const firstLoadOut = before?.fetchStatus === "fetching" && before.data === undefined;
  // A cached read still out must not land after (and over) the fresh one.
  await queryClient.cancelQueries({ queryKey: key, exact: true });
  let entries: CatalogueEntry[];
  try {
    entries = await readFresh(eventId);
  } catch (e) {
    if (firstLoadOut && queryClient.getQueryData(key) === undefined) {
      void queryClient.refetchQueries({ queryKey: key, exact: true });
    }
    throw e;
  }
  queryClient.setQueryData(key, entries);
  return entries;
}

/** The uncached read, given up (and its request aborted) after the timeout. */
async function readFresh(eventId: string): Promise<CatalogueEntry[]> {
  const ctrl = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      ctrl.abort();
      reject(new Error("guestFieldCatalogueTimeout"));
    }, FRESH_CATALOGUE_TIMEOUT_MS);
  });
  try {
    return await Promise.race([
      getGuestFieldCatalogue(eventId, { bypassPreview: true, fresh: true, signal: ctrl.signal }),
      timeout,
    ]);
  } finally {
    clearTimeout(timer);
  }
}
